import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
    Users,
    ExternalLink,
    ThumbsUp,
    ThumbsDown,
    MessageCircleQuestion,
    Sparkles,
} from "lucide-react";

const STATUS_STYLE = {
    pending: "bg-gold-100 text-gold-800 border border-gold-200",
    approved: "bg-emerald-100 text-emerald-800 border border-emerald-200",
    rejected: "bg-red-50 text-red-700 border border-red-200",
    info_requested: "bg-navy-100 text-navy-700 border border-navy-200",
};
const STATUS_LABEL = {
    pending: "Pending review",
    approved: "Approved",
    rejected: "Declined",
    info_requested: "Awaiting info",
};

const TABS = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Declined" },
];

export default function AdminAffiliateApplications() {
    const [rows, setRows] = useState([]);
    const [tab, setTab] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/affiliate-applications");
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(() => {
        if (tab === "all") return rows;
        return rows.filter((r) => r.status === tab);
    }, [rows, tab]);

    const counts = useMemo(() => {
        const c = { all: rows.length, pending: 0, approved: 0, rejected: 0 };
        rows.forEach((r) => {
            if (r.status in c) c[r.status] += 1;
        });
        return c;
    }, [rows]);

    return (
        <>
            <PageHeader
                eyebrow="Admin"
                title="Affiliate applications"
                description="Review each prospective affiliate's presence and content before granting them access to the dashboard, referral tools and discount codes."
                testid="admin-affiliate-apps-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="flex gap-1.5" data-testid="affiliate-app-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`aff-tab-${t.key}`}
                            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest transition ${
                                tab === t.key
                                    ? "bg-navy-700 text-cream"
                                    : "bg-white border border-navy-100 text-navy-500 hover:bg-cream"
                            }`}
                        >
                            {t.label}{" "}
                            <span className="ml-1 opacity-70">
                                {counts[t.key] ?? 0}
                            </span>
                        </button>
                    ))}
                </div>

                <div
                    data-testid="affiliate-app-table"
                    className="rounded-2xl bg-white border border-navy-100 overflow-hidden"
                >
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-cream">
                                <TableHead>Applicant</TableHead>
                                <TableHead>Niche</TableHead>
                                <TableHead>Socials</TableHead>
                                <TableHead>Submitted</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Review</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={6} className="py-8 text-center text-navy-500">
                                        Loading applications…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} className="py-14 text-center text-navy-400">
                                        <Users className="mx-auto mb-2" />
                                        No applications in this bucket yet.
                                    </TableCell>
                                </TableRow>
                            )}
                            {filtered.map((a) => (
                                <TableRow
                                    key={a.id}
                                    data-testid={`aff-app-row-${a.id}`}
                                    className="cursor-pointer hover:bg-cream/60"
                                    onClick={() => setSelected(a)}
                                >
                                    <TableCell>
                                        <div className="font-medium text-navy-700">
                                            {a.full_name}
                                        </div>
                                        <div className="text-[11px] text-navy-500">
                                            {a.email}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-navy-500 text-sm">
                                        {a.niche || "—"}
                                    </TableCell>
                                    <TableCell className="text-navy-500 text-xs">
                                        {Object.keys(a.socials || {}).length}
                                    </TableCell>
                                    <TableCell className="text-navy-500 text-xs">
                                        {a.submitted_at
                                            ? new Date(a.submitted_at).toLocaleDateString()
                                            : "—"}
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={`${STATUS_STYLE[a.status] || ""}`}>
                                            {STATUS_LABEL[a.status] || a.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            data-testid={`aff-app-review-${a.id}`}
                                            className="rounded-full h-8 border-navy-200 text-xs"
                                        >
                                            Open
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <ReviewDialog
                app={selected}
                onClose={() => setSelected(null)}
                onSaved={async () => {
                    await load();
                    setSelected(null);
                }}
            />
        </>
    );
}

function ReviewDialog({ app, onClose, onSaved }) {
    const [notes, setNotes] = useState("");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(null); // "approve" | "reject" | "info" | "notes"

    // Reset local state when a new app is selected.
    useEffect(() => {
        if (!app) return;
        setNotes(app.admin_notes || "");
        setReason(app.rejection_reason || "");
    }, [app]);

    const patch = async (payload, kind) => {
        setBusy(kind);
        try {
            await api.patch(`/admin/affiliate-applications/${app.id}`, payload);
            toast.success(
                kind === "approve"
                    ? "Approved — welcome email queued"
                    : kind === "reject"
                    ? "Application declined — email queued"
                    : kind === "info"
                    ? "Info-request email queued"
                    : "Notes saved"
            );
            await onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };

    if (!app) return null;

    return (
        <Dialog open={!!app} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                data-testid="aff-app-review-dialog"
                className="max-w-3xl bg-white max-h-[92vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-navy-700 flex items-center gap-2">
                        <Sparkles size={18} className="text-gold-600" />
                        {app.full_name}
                    </DialogTitle>
                    <DialogDescription>
                        Review the applicant&rsquo;s socials, content and motivation below.
                        Approving auto-creates the affiliate account and sends a welcome
                        email with a password-set link.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    <SectionGrid
                        rows={[
                            ["Email", app.email],
                            ["Business", app.business_name || "—"],
                            [
                                "Website",
                                app.website ? (
                                    <a
                                        href={app.website}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-navy-700 underline underline-offset-2"
                                    >
                                        {app.website}
                                    </a>
                                ) : (
                                    "—"
                                ),
                            ],
                            ["Niche", app.niche || "—"],
                            [
                                "Submitted",
                                app.submitted_at
                                    ? new Date(app.submitted_at).toLocaleString()
                                    : "—",
                            ],
                            [
                                "Status",
                                <Badge
                                    key="status"
                                    className={`${STATUS_STYLE[app.status] || ""}`}
                                >
                                    {STATUS_LABEL[app.status] || app.status}
                                </Badge>,
                            ],
                        ]}
                    />

                    <Panel title="Social profiles">
                        <div className="grid sm:grid-cols-2 gap-2">
                            {Object.entries(app.socials || {}).map(([k, v]) => (
                                <a
                                    key={k}
                                    href={v}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`aff-app-social-${k}`}
                                    className="flex items-center justify-between gap-2 rounded-xl bg-cream border border-navy-100 px-3 py-2 hover:border-navy-300 transition"
                                >
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-navy-400">
                                            {k}
                                        </div>
                                        <div className="text-sm text-navy-700 truncate max-w-[240px]">
                                            {v}
                                        </div>
                                    </div>
                                    <ExternalLink size={14} className="text-navy-400 flex-shrink-0" />
                                </a>
                            ))}
                            {Object.keys(app.socials || {}).length === 0 && (
                                <div className="text-navy-400 text-sm">
                                    No social profiles supplied.
                                </div>
                            )}
                        </div>
                    </Panel>

                    <Panel title="Recent content">
                        <div className="space-y-2">
                            {(app.content_links || []).map((l, i) => (
                                <a
                                    key={`${l}-${i}`}
                                    href={l}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`aff-app-content-${i}`}
                                    className="flex items-center justify-between gap-2 rounded-xl bg-cream border border-navy-100 px-3 py-2 hover:border-navy-300 transition text-sm text-navy-700 truncate"
                                >
                                    <span className="truncate">{l}</span>
                                    <ExternalLink size={14} className="text-navy-400 flex-shrink-0" />
                                </a>
                            ))}
                            {(app.content_links || []).length === 0 && (
                                <div className="text-navy-400 text-sm">None provided.</div>
                            )}
                        </div>
                    </Panel>

                    <Panel title="Why promote Perx">
                        <p className="text-sm text-navy-700 whitespace-pre-line">
                            {app.why_promote}
                        </p>
                    </Panel>
                    <Panel title="Audience">
                        <p className="text-sm text-navy-700 whitespace-pre-line">
                            {app.audience_description}
                        </p>
                    </Panel>
                    {app.prior_experience && (
                        <Panel title="Prior affiliate experience">
                            <p className="text-sm text-navy-700 whitespace-pre-line">
                                {app.prior_experience}
                            </p>
                        </Panel>
                    )}

                    <Panel title="Admin notes (internal only)">
                        <Textarea
                            data-testid="aff-app-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="resize-none"
                            placeholder="Anything the rest of the team should know about this applicant…"
                        />
                        <div className="mt-2 text-right">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={busy === "notes"}
                                onClick={() => patch({ admin_notes: notes }, "notes")}
                                data-testid="aff-app-save-notes"
                                className="rounded-full border-navy-200 text-xs"
                            >
                                {busy === "notes" ? "Saving…" : "Save notes"}
                            </Button>
                        </div>
                    </Panel>

                    <Panel title="Rejection / follow-up message (optional)">
                        <Textarea
                            data-testid="aff-app-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            className="resize-none"
                            placeholder="Customise the message sent when you decline or request more info."
                        />
                    </Panel>
                </div>

                <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap pt-2 border-t border-navy-100 mt-4">
                    <Button
                        variant="outline"
                        onClick={() => patch({ status: "info_requested", rejection_reason: reason, admin_notes: notes }, "info")}
                        disabled={busy}
                        data-testid="aff-app-info-btn"
                        className="rounded-full border-navy-200"
                    >
                        <MessageCircleQuestion size={14} className="mr-1.5" />
                        Request more info
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => {
                            if (
                                !window.confirm(
                                    "Send rejection email? A customisable message from the field above will be included."
                                )
                            )
                                return;
                            patch(
                                { status: "rejected", rejection_reason: reason, admin_notes: notes },
                                "reject"
                            );
                        }}
                        disabled={busy || app.status === "rejected"}
                        data-testid="aff-app-reject-btn"
                        className="rounded-full border-red-200 text-red-700 hover:bg-red-50"
                    >
                        <ThumbsDown size={14} className="mr-1.5" />
                        Decline
                    </Button>
                    <Button
                        onClick={() => {
                            if (
                                !window.confirm(
                                    "Approve this application? An affiliate account will be created and a welcome email sent."
                                )
                            )
                                return;
                            patch({ status: "approved", admin_notes: notes }, "approve");
                        }}
                        disabled={busy || app.status === "approved"}
                        data-testid="aff-app-approve-btn"
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                        <ThumbsUp size={14} className="mr-1.5" />
                        Approve &amp; create account
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SectionGrid({ rows }) {
    return (
        <div className="rounded-2xl bg-cream border border-navy-100 p-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {rows.map(([k, v], i) => (
                <div key={`${k}-${i}`} className="flex justify-between gap-4 py-1 border-b last:border-b-0 border-navy-100/40">
                    <span className="text-navy-400 uppercase tracking-widest text-[10px]">
                        {k}
                    </span>
                    <span className="text-navy-700 text-right break-words">{v}</span>
                </div>
            ))}
        </div>
    );
}

function Panel({ title, children }) {
    return (
        <div className="rounded-2xl border border-navy-100 bg-white p-4">
            <div className="text-[11px] uppercase tracking-widest text-gold-600 mb-2">
                {title}
            </div>
            {children}
        </div>
    );
}
